import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "header_room_summary" }
const _hoisted_2 = { class: "type_badge" }
const _hoisted_3 = { class: "room_body" }
const _hoisted_4 = { class: "room_summary" }
const _hoisted_5 = { class: "type_badge" }
const _hoisted_6 = {
  key: 0,
  class: "screen_share screen_share_mobile"
}
const _hoisted_7 = { class: "screen_stage" }
const _hoisted_8 = { class: "screen_owner" }
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 1,
  class: "screen_share screen_share_pc"
}
const _hoisted_11 = { class: "screen_stage" }
const _hoisted_12 = { class: "screen_owner" }
const _hoisted_13 = ["src"]
const _hoisted_14 = {
  key: 2,
  class: "list_status"
}
const _hoisted_15 = {
  key: 0,
  class: "member_panel"
}
const _hoisted_16 = { class: "member_panel_header" }
const _hoisted_17 = { class: "member_panel_body" }
const _hoisted_18 = { class: "member_avatar" }
const _hoisted_19 = ["src", "onErrorOnce"]
const _hoisted_20 = ["src"]
const _hoisted_21 = { class: "member_info" }
const _hoisted_22 = { class: "member_name" }
const _hoisted_23 = ["src"]

import {
  getConferenceTouristsInfoApi,
  getConferenceUserAllApi,
  touristsLeaveConferenceApi,
} from '@/api/meeting';
import useOpenApp from '@/hooks/useOpenApp';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MeetingRoomFooter from './components/MeetingRoomFooter.vue';
import MeetingRoomGuestTip from './components/MeetingRoomGuestTip.vue';
import MeetingRoomHeader from './components/MeetingRoomHeader.vue';
import MeetingRoomModals from './components/MeetingRoomModals.vue';
import MeetingRoomUserGrid from './components/MeetingRoomUserGrid.vue';
import {
  getDefaultAvatar,
  getRoleText,
  getUserAvatar,
  getUserIdentity,
  getUserKey,
  getUserName,
  getUserPledgeLogo,
  isMuted,
} from './components/meetingRoomUsers';
import type { MeetingUser } from './components/meetingRoomUsers';
import i18n from '@/language';
import {
  hasGuestRtcSession,
  leaveGuestRtcSession,
  onGuestRtcAudioStateChange,
  onGuestRtcSessionReplaced,
  onGuestRtcTokenExpired,
  onGuestRtcUserStateChange,
  onGuestRtcVideoStateChange,
  playGuestRtcVideo,
  removeGuestRtcPayload,
  resumeGuestRtcAudio,
} from '@/utils/meetingGuestRtc';
import ScreenFullscreenIcon from '@/assets/img/meeting-room/screen-fullscreen.png';
import ScreenFullscreenActiveIcon from '@/assets/img/meeting-room/screen-fullscreen-active.png';
import Avatar1 from '@/assets/img/meeting-room/avatar-1.png';
import Avatar2 from '@/assets/img/meeting-room/avatar-2.png';
import Avatar3 from '@/assets/img/meeting-room/avatar-3.png';
import Avatar4 from '@/assets/img/meeting-room/avatar-4.png';
import Avatar5 from '@/assets/img/meeting-room/avatar-5.png';
import UserMicMutedIcon from '@/assets/img/meeting-room/user-mic-muted.png';
import UserMicOnIcon from '@/assets/img/meeting-room/user-mic-on.png';

const pageSize = 100;
const POLL_INTERVAL = 60000;
const GUEST_TIP_DURATION = 5000;

export default /*@__PURE__*/_defineComponent({
  __name: 'meetingRome',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { openApp } = useOpenApp();
const t = i18n.global.t;

const MEETING_ENDED_STATUS_LIST = [
  '3',
  'over',
  'end',
  'ended',
  'finish',
  'finished',
  'close',
  'closed',
  'stop',
  'stopped',
  'terminated',
];
const loadingInfo = ref(false);
const loadingUsers = ref(false);
const meetingInfo = ref<any>({});
const users = ref<MeetingUser[]>([]);
const showGuestTip = ref(true);
const showLoginPopup = ref(false);
const showLeavePopup = ref(false);
const rtcJoined = ref(true);
const hasSharedScreen = ref(false);
const sharedScreenUid = ref<string | number>();
const pcScreenContainerRef = ref<HTMLElement | null>(null);
const mobileScreenContainerRef = ref<HTMLElement | null>(null);
const screenFullscreen = ref(false);
const isPcScreen = ref(false);
const speakingRtcUids = ref<string[]>([]);
const unmutedRtcUids = ref<string[]>([]);
const showAllMembers = ref(false);
const autoLeavingMeeting = ref(false);
const serverTimeOffset = ref(0);
const originalTheme = ref<string | null>(null);
const originalColorScheme = ref('');
let guestTipTimer: number | undefined;
let clockTimer: number | undefined;
let pollTimer: number | undefined;
let meetingEndedConfirmTimers: number[] = [];
let unsubscribeAudioState: (() => void) | undefined;
let unsubscribeVideoState: (() => void) | undefined;
let unsubscribeSessionReplaced: (() => void) | undefined;
let unsubscribeTokenExpired: (() => void) | undefined;
let unsubscribeUserStateChange: (() => void) | undefined;
let screenMediaQueryList: MediaQueryList | undefined;
const now = ref(Date.now());
const defaultAvatars = [Avatar1, Avatar2, Avatar3, Avatar4, Avatar5];

const conferenceId = computed(() => String(route.query.id ?? ''));

const meetingTitle = computed(() => {
  return (
    meetingInfo.value?.roomName ||
    meetingInfo.value?.conferenceName ||
    meetingInfo.value?.title ||
    t('会议')
  );
});

const shortMeetingTitle = computed(() => {
  const title = `${meetingTitle.value}`;
  return title.length > 12 ? `${title.slice(0, 12)}...` : title;
});

const conferenceTypeText = computed(() => {
  const type = Number(meetingInfo.value?.conferenceType);
  if (type === 2) return '轻演';
  if (type === 3) return '高级';
  return '语音';
});

const sharingUserName = computed(() => {
  const sharedUser = users.value.find((user, index) => {
    const key = getUserKey(user, index);
    return String(key) === String(sharedScreenUid.value);
  });

  const name = sharedUser ? getUserName(sharedUser) || t('用户昵称xxx') : t('用户昵称xxx');
  return name.length > 8 ? `${name.slice(0, 8)}xxx` : name;
});

const listeningCount = computed(() => {
  return meetingInfo.value?.realPublishers ?? users.value.length;
});

const audienceCount = computed(() => {
  return meetingInfo.value?.touristsCount ?? 0;
});

const memberCount = computed(() => {
  const count = Number(meetingInfo.value?.realPublishers);

  return Number.isFinite(count) && count >= 0 ? count : users.value.length;
});

const shouldShowMemberPanelMore = computed(() => {
  return users.value.length === pageSize && memberCount.value > users.value.length;
});


const meetingStartTime = computed(() =>
  getMeetingTimestamp([
    'startTime',
  ]),
);

const meetingEndTime = computed(() =>
  getMeetingTimestamp([
    'endTime',
  ]),
);

const currentSysTime = computed(() =>
  getMeetingTimestamp([
    'currentSysTime',
  ]),
);

const plannedDurationMinutes = computed(() => {
  const duration = Number(
    meetingInfo.value?.duration ??
      meetingInfo.value?.minutes ??
      meetingInfo.value?.conferenceDuration ??
      meetingInfo.value?.roomDuration,
  );
  return Number.isFinite(duration) && duration > 0 ? duration : 0;
});

const guestTipDuration = computed(() => meetingInfo.value?.viewDuration ?? '');

const effectiveStartTime = computed(() => {
  if (meetingStartTime.value) return meetingStartTime.value;
  if (meetingEndTime.value && plannedDurationMinutes.value) {
    return meetingEndTime.value - plannedDurationMinutes.value * 60 * 1000;
  }
  return 0;
});

const effectiveEndTime = computed(() => {
  if (meetingEndTime.value) return meetingEndTime.value;
  if (meetingStartTime.value && plannedDurationMinutes.value) {
    return meetingStartTime.value + plannedDurationMinutes.value * 60 * 1000;
  }
  return 0;
});

const remainingMilliseconds = computed(() => {
  if (!effectiveEndTime.value) return 0;
  return Math.max(0, effectiveEndTime.value - calibratedNow.value);
});

const calibratedNow = computed(() => now.value + serverTimeOffset.value);

const elapsedSeconds = computed(() => {
  const start = effectiveStartTime.value;
  if (!start) return 0;

  const end = effectiveEndTime.value;
  const current = end ? Math.min(calibratedNow.value, end) : calibratedNow.value;
  return Math.max(0, Math.floor((current - start) / 1000));
});

const elapsedText = computed(() => {
  return formatDuration(elapsedSeconds.value);
});

const warningMinutes = computed(() => {
  if (!remainingMilliseconds.value) return 0;
  return Math.max(1, Math.ceil(remainingMilliseconds.value / 60000));
});

const shouldShowEndWarning = computed(() => {
  return (
    remainingMilliseconds.value > 0 &&
    remainingMilliseconds.value <= 10 * 60 * 1000
  );
});

function getMeetingTimestamp(keys: string[]) {
  const keySet = new Set(keys);
  const visited = new Set<any>();

  function findTimestamp(source: any, depth = 0): number {
    if (!source || typeof source !== 'object' || depth > 5 || visited.has(source)) {
      return 0;
    }

    visited.add(source);

    for (const key of keys) {
      const timestamp = normalizeTimestamp(source[key]);
      if (timestamp) return timestamp;
    }

    for (const [key, value] of Object.entries(source)) {
      if (keySet.has(key)) {
        const timestamp = normalizeTimestamp(value);
        if (timestamp) return timestamp;
      }

      if (value && typeof value === 'object') {
        const timestamp = findTimestamp(value, depth + 1);
        if (timestamp) return timestamp;
      }
    }

    return 0;
  }

  return findTimestamp(meetingInfo.value);
}

function normalizeTimestamp(value: any) {
  if (value === null || value === undefined || value === '') return 0;

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (value < 10000000000) return value * 1000;
    while (value > 9999999999999) value = Math.floor(value / 1000);
    return value;
  }

  const text = String(value).trim();
  if (!text) return 0;

  if (/^\d+$/.test(text)) {
    let timestamp = Number(text);
    if (!Number.isFinite(timestamp) || timestamp <= 0) return 0;
    if (timestamp < 10000000000) return timestamp * 1000;
    while (timestamp > 9999999999999) timestamp = Math.floor(timestamp / 1000);
    return timestamp;
  }

  const timestamp = new Date(text).getTime();
  if (Number.isFinite(timestamp)) return timestamp;

  const normalizedTimestamp = new Date(text.replace(/-/g, '/')).getTime();
  return Number.isFinite(normalizedTimestamp) ? normalizedTimestamp : 0;
}

function formatDuration(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((item) => `${item}`.padStart(2, '0'))
    .join(':');
}

function normalizeResult(res: any) {
  return res?.value?.result ?? res?.value?.data ?? res?.value ?? {};
}

function normalizeUserList(res: any) {
  const result = normalizeResult(res);
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.records)) return result.records;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

function getMeetingStatus(info: any) {
  return String(
    info?.roomStatus ??
      info?.status ??
      info?.conferenceStatus ??
      info?.meetingStatus ??
      info?.state ??
      '',
  ).toLowerCase();
}

function isMeetingEnded(info: any) {
  return MEETING_ENDED_STATUS_LIST.includes(getMeetingStatus(info));
}

async function getMeetingInfo(options: { silent?: boolean } = {}) {
  if (!conferenceId.value) return;
  if (!options.silent) {
    loadingInfo.value = true;
    showGuestTip.value = true;
    if (guestTipTimer) window.clearTimeout(guestTipTimer);
  }
  try {
    const res = await getConferenceTouristsInfoApi(conferenceId.value);
    meetingInfo.value = normalizeResult(res);
    now.value = Date.now();
    if (currentSysTime.value) {
      serverTimeOffset.value = currentSysTime.value - now.value;
    }
    if (isMeetingEnded(meetingInfo.value)) {
      autoLeaveGuestMeeting('meeting-ended');
    }
  } catch (error) {
  } finally {
    if (!options.silent) {
      loadingInfo.value = false;
      startGuestTipTimer();
    }
  }
}

async function getUsers(options: { silent?: boolean } = {}) {
  if (!conferenceId.value || loadingUsers.value) return;
  if (!options.silent) {
    loadingUsers.value = true;
  }
  try {
    const res = await getConferenceUserAllApi({
      id: conferenceId.value,
      pageNum: 1,
      pageSize,
    });
    const list = normalizeUserList(res);
    users.value = list;
  } catch (error) {
    users.value = [];
  } finally {
    if (!options.silent) {
      loadingUsers.value = false;
    }
  }
}

function refreshMeetingData(options: { silent?: boolean } = {}) {
  getMeetingInfo(options);
  getUsers(options);
}

function restartMeetingPoll() {
  if (pollTimer) window.clearInterval(pollTimer);
  pollTimer = window.setInterval(() => {
    refreshMeetingData({ silent: true });
  }, POLL_INTERVAL);
}

function refreshMeetingDataAndRestartPoll() {
  refreshMeetingData({ silent: true });
  restartMeetingPoll();
}

function clearMeetingEndedConfirmTimers() {
  meetingEndedConfirmTimers.forEach((timer) => window.clearTimeout(timer));
  meetingEndedConfirmTimers = [];
}

function confirmMeetingEndedSoon() {
  clearMeetingEndedConfirmTimers();

  [0, 1000, 3000, 6000, 10000, 15000].forEach((delay) => {
    const timer = window.setTimeout(() => {
      if (autoLeavingMeeting.value) return;
      getMeetingInfo({ silent: true });
    }, delay);
    meetingEndedConfirmTimers.push(timer);
  });
}

function showLoginTipPopup() {
  showLoginPopup.value = true;
}

function renderSharedScreen(retryCount = 0) {
  nextTick(() => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const playTarget = getScreenPlayTarget();
        if (!playTarget) {
          if (retryCount < 20) {
            window.setTimeout(() => renderSharedScreen(retryCount + 1), 120);
          }
          return;
        }

        const rect = playTarget.getBoundingClientRect();
        if (!rect.width || !rect.height) {
          if (retryCount < 20) {
            window.setTimeout(() => renderSharedScreen(retryCount + 1), 120);
          }
          return;
        }

        console.info('[meeting] render shared screen', {
          target: playTarget.tagName,
          isPc: isPcScreen.value,
          width: rect.width,
          height: rect.height,
          retryCount,
        });
        const played = playGuestRtcVideo(playTarget);
        if (!played && retryCount < 20) {
          window.setTimeout(() => renderSharedScreen(retryCount + 1), 120);
        }
      });
    });
  });
}

function getScreenPlayTarget() {
  if (isPcScreen.value) {
    return pcScreenContainerRef.value;
  }

  return mobileScreenContainerRef.value;
}

function syncScreenBreakpoint(event?: MediaQueryListEvent | MediaQueryList) {
  isPcScreen.value = Boolean(event?.matches);

  if (hasSharedScreen.value) {
    renderSharedScreen();
  }
}

function toggleScreenFullscreen() {
  screenFullscreen.value = !screenFullscreen.value;
  renderSharedScreen();
}

function showAllMemberList() {
  showAllMembers.value = true;
}

function closeAllMemberList() {
  showAllMembers.value = false;
}

function handleMemberAvatarError(event: Event, user: MeetingUser, index: number) {
  const target = event.target as HTMLImageElement;
  target.src = getDefaultAvatar(user, index, defaultAvatars);
}

function getMemberName(user: MeetingUser) {
  return getUserName(user) || t('用户昵称');
}

function memberMuted(user: MeetingUser, index: number) {
  return isMuted(user, index, unmutedRtcUids.value);
}

function handleLeaveClick() {
  showLeavePopup.value = true;
}

async function reportTouristsLeave() {
  if (!conferenceId.value) return;

  try {
    await touristsLeaveConferenceApi(conferenceId.value);
  } catch (error) {
    console.warn('[meeting] tourists leave failed', error);
  }
}

async function leaveMeeting() {
  showLeavePopup.value = false;
  await reportTouristsLeave();
  await leaveGuestRtcSession();

  if (conferenceId.value) {
    removeGuestRtcPayload(conferenceId.value);
  }

  router.replace({
    name: 'Meeting',
    query: route.query,
  });
}

function startGuestTipTimer() {
  if (guestTipTimer) window.clearTimeout(guestTipTimer);
  guestTipTimer = window.setTimeout(() => {
    showGuestTip.value = false;
  }, GUEST_TIP_DURATION);
}

async function autoLeaveGuestMeeting(
  reason: string,
  options: { reportLeave?: boolean } = {},
) {
  if (autoLeavingMeeting.value) return;

  autoLeavingMeeting.value = true;
  clearMeetingEndedConfirmTimers();
  console.info('[meeting] auto leave guest meeting', { reason });

  if (options.reportLeave) {
    await reportTouristsLeave();
  }

  await leaveGuestRtcSession();

  if (conferenceId.value) {
    removeGuestRtcPayload(conferenceId.value);
  }

  router.replace({
    name: 'Meeting',
    query: route.query,
  });
}

function forceLightTheme() {
  const root = document.documentElement;
  originalTheme.value = root.getAttribute('data-theme');
  originalColorScheme.value = root.style.colorScheme;
  root.setAttribute('data-theme', 'light');
  root.style.colorScheme = 'light';
}

function restoreTheme() {
  const root = document.documentElement;

  if (originalTheme.value === null) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', originalTheme.value);
  }

  root.style.colorScheme = originalColorScheme.value;
}

onMounted(() => {
  forceLightTheme();
  screenMediaQueryList = window.matchMedia('(min-width: 768px)');
  syncScreenBreakpoint(screenMediaQueryList);

  if (screenMediaQueryList.addEventListener) {
    screenMediaQueryList.addEventListener('change', syncScreenBreakpoint);
  } else {
    screenMediaQueryList.addListener(syncScreenBreakpoint);
  }

  if (!hasGuestRtcSession(conferenceId.value)) {
    router.replace({
      name: 'Meeting',
      query: route.query,
    });
    return;
  }

  refreshMeetingData();
  unsubscribeAudioState = onGuestRtcAudioStateChange((state) => {
    speakingRtcUids.value = state.speakingUids.map((uid) => String(uid));
    unmutedRtcUids.value = state.unmutedUids.map((uid) => String(uid));
  });
  unsubscribeVideoState = onGuestRtcVideoStateChange((state) => {
    hasSharedScreen.value = state.hasVideo;
    sharedScreenUid.value = state.uid;

    if (!state.hasVideo) {
      screenFullscreen.value = false;
      return;
    }

    renderSharedScreen();
  });
  unsubscribeSessionReplaced = onGuestRtcSessionReplaced(() => {
    autoLeaveGuestMeeting('session-replaced');
  });
  unsubscribeTokenExpired = onGuestRtcTokenExpired((state) => {
    if (!state.conferenceId || state.conferenceId === conferenceId.value) {
      autoLeaveGuestMeeting(state.reason || 'token-expired', {
        reportLeave: true,
      });
    }
  });
  unsubscribeUserStateChange = onGuestRtcUserStateChange((state) => {
    if (!state.conferenceId || state.conferenceId === conferenceId.value) {
      if (state.reason.startsWith('user-left')) {
        confirmMeetingEndedSoon();
      }
      refreshMeetingDataAndRestartPoll();
    }
  });
  clockTimer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
  restartMeetingPoll();
});

watch(conferenceId, async (id, oldId) => {
  if (!id || id === oldId) return;
  if (!hasGuestRtcSession(id)) {
    await leaveGuestRtcSession();
    router.replace({
      name: 'Meeting',
      query: route.query,
    });
    return;
  }

  users.value = [];
  refreshMeetingData();
});

onUnmounted(() => {
  if (guestTipTimer) window.clearTimeout(guestTipTimer);
  if (clockTimer) window.clearInterval(clockTimer);
  if (pollTimer) window.clearInterval(pollTimer);
  clearMeetingEndedConfirmTimers();
  if (unsubscribeAudioState) unsubscribeAudioState();
  if (unsubscribeVideoState) unsubscribeVideoState();
  if (unsubscribeSessionReplaced) unsubscribeSessionReplaced();
  if (unsubscribeTokenExpired) unsubscribeTokenExpired();
  if (unsubscribeUserStateChange) unsubscribeUserStateChange();
  if (screenMediaQueryList) {
    if (screenMediaQueryList.removeEventListener) {
      screenMediaQueryList.removeEventListener('change', syncScreenBreakpoint);
    } else {
      screenMediaQueryList.removeListener(syncScreenBreakpoint);
    }
  }
  if (conferenceId.value) {
    removeGuestRtcPayload(conferenceId.value);
  }
  restoreTheme();
  leaveGuestRtcSession();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["meeting_room", {
      has_end_warning: shouldShowEndWarning.value,
      has_shared_screen: hasSharedScreen.value,
      screen_fullscreen: screenFullscreen.value,
      show_member_panel: showAllMembers.value,
    }]),
    onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(resumeGuestRtcAudio) && _unref(resumeGuestRtcAudio)(...args)))
  }, [
    _createVNode(MeetingRoomHeader, {
      title: shortMeetingTitle.value,
      "elapsed-text": elapsedText.value,
      "rtc-joined": rtcJoined.value,
      "show-warning": shouldShowEndWarning.value,
      "warning-minutes": warningMinutes.value,
      "screen-fullscreen": screenFullscreen.value,
      onLeave: handleLeaveClick
    }, {
      extra: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.$t(conferenceTypeText.value)), 1),
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('{listenCount} 正在收听 · {audienceCount} 旁听', {
                listenCount: listeningCount.value,
                audienceCount: audienceCount.value,
              })), 1)
        ])
      ]),
      _: 1
    }, 8, ["title", "elapsed-text", "rtc-joined", "show-warning", "warning-minutes", "screen-fullscreen"]),
    _createElementVNode("main", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t(conferenceTypeText.value)), 1),
        _createElementVNode("span", null, _toDisplayString(_ctx.$t('{listenCount} 正在收听 · {audienceCount} 旁听', {
              listenCount: listeningCount.value,
              audienceCount: audienceCount.value,
            })), 1)
      ]),
      (hasSharedScreen.value && !isPcScreen.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, _toDisplayString(_ctx.$t('{name}的屏幕共享', { name: sharingUserName.value })), 1),
              _createElementVNode("div", {
                ref_key: "mobileScreenContainerRef",
                ref: mobileScreenContainerRef,
                class: "screen_player mobile_screen_player"
              }, null, 512),
              _createElementVNode("button", {
                class: "screen_full_button",
                type: "button",
                onClick: _withModifiers(toggleScreenFullscreen, ["stop"])
              }, [
                _createElementVNode("img", {
                  src: 
                screenFullscreen.value
                  ? _unref(ScreenFullscreenActiveIcon)
                  : _unref(ScreenFullscreenIcon)
              ,
                  alt: ""
                }, null, 8, _hoisted_9)
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      (hasSharedScreen.value && isPcScreen.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t('{name}的屏幕共享', { name: sharingUserName.value })), 1),
              _createElementVNode("div", {
                ref_key: "pcScreenContainerRef",
                ref: pcScreenContainerRef,
                class: "screen_player pc_screen_player"
              }, null, 512),
              _createElementVNode("button", {
                class: "screen_full_button",
                type: "button",
                onClick: _withModifiers(toggleScreenFullscreen, ["stop"])
              }, [
                _createElementVNode("img", {
                  src: 
                screenFullscreen.value
                  ? _unref(ScreenFullscreenActiveIcon)
                  : _unref(ScreenFullscreenIcon)
              ,
                  alt: ""
                }, null, 8, _hoisted_13)
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(MeetingRoomUserGrid, {
        users: users.value,
        "total-user-count": memberCount.value,
        "page-size": pageSize,
        "has-shared-screen": hasSharedScreen.value,
        "screen-fullscreen": screenFullscreen.value,
        "speaking-rtc-uids": speakingRtcUids.value,
        "unmuted-rtc-uids": unmutedRtcUids.value,
        onShowAll: showAllMemberList,
        onShowMore: showLoginTipPopup
      }, null, 8, ["users", "total-user-count", "has-shared-screen", "screen-fullscreen", "speaking-rtc-uids", "unmuted-rtc-uids"]),
      (loadingUsers.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(_ctx.$t('加载中...')), 1))
        : _createCommentVNode("", true)
    ]),
    (showAllMembers.value)
      ? (_openBlock(), _createElementBlock("aside", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("strong", null, _toDisplayString(_ctx.$t('成员')) + "（" + _toDisplayString(memberCount.value) + "）", 1),
            _createElementVNode("button", {
              type: "button",
              onClick: closeAllMemberList
            }, _cache[4] || (_cache[4] = [
              _createElementVNode("span", null, null, -1),
              _createElementVNode("span", null, null, -1)
            ]))
          ]),
          _createElementVNode("div", _hoisted_17, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(users.value, (user, index) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "member_panel_item",
                key: _unref(getUserKey)(user, index)
              }, [
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("img", {
                    src: _unref(getUserAvatar)(user, index, defaultAvatars),
                    onErrorOnce: ($event: any) => (handleMemberAvatarError($event, user, index)),
                    alt: ""
                  }, null, 40, _hoisted_19),
                  (_unref(getUserIdentity)(user) !== 0)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(["member_mic", { muted: memberMuted(user, index) }])
                      }, [
                        _createElementVNode("img", {
                          src: memberMuted(user, index) ? _unref(UserMicMutedIcon) : _unref(UserMicOnIcon),
                          alt: ""
                        }, null, 8, _hoisted_20)
                      ], 2))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("span", null, _toDisplayString(getMemberName(user)), 1),
                    (_unref(getUserPledgeLogo)(user))
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: _unref(getUserPledgeLogo)(user),
                          alt: ""
                        }, null, 8, _hoisted_23))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", {
                    class: _normalizeClass(["member_role", {
                speaker: _unref(getUserIdentity)(user) === 3,
                audience: _unref(getUserIdentity)(user) === 0,
              }])
                  }, _toDisplayString(_ctx.$t(_unref(getRoleText)(user, index))), 3)
                ])
              ]))
            }), 128)),
            (shouldShowMemberPanelMore.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "member_panel_more",
                  type: "button",
                  onClick: showLoginTipPopup
                }, _toDisplayString(_ctx.$t('查看更多')), 1))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (showGuestTip.value && !screenFullscreen.value)
      ? (_openBlock(), _createBlock(MeetingRoomGuestTip, {
          key: 1,
          "tourist-duration": guestTipDuration.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (showGuestTip.value = false)),
          onLogin: _unref(openApp)
        }, null, 8, ["tourist-duration", "onLogin"]))
      : _createCommentVNode("", true),
    _createVNode(MeetingRoomFooter, {
      "screen-fullscreen": screenFullscreen.value,
      onAction: showLoginTipPopup,
      onLeave: handleLeaveClick
    }, null, 8, ["screen-fullscreen"]),
    _createVNode(MeetingRoomModals, {
      "show-login": showLoginPopup.value,
      "onUpdate:showLogin": _cache[1] || (_cache[1] = ($event: any) => ((showLoginPopup).value = $event)),
      "show-leave": showLeavePopup.value,
      "onUpdate:showLeave": _cache[2] || (_cache[2] = ($event: any) => ((showLeavePopup).value = $event)),
      onLogin: _unref(openApp),
      onLeave: leaveMeeting
    }, null, 8, ["show-login", "show-leave", "onLogin"])
  ], 2))
}
}

})